import { auth } from './lib/firebase';
import {
  applyActionCode,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import {
  getDashboardPermissions,
  getUserProfile,
} from './dataProvider/dataProvider';
import { canAccessWithPermissions } from './rbac/canAccessWithPermissions';
import { API_URL } from './constants';
import { AuthProvider, fetchUtils, UserIdentity } from 'ra-core';

export interface ExtendedAuthProvider extends AuthProvider {
  getAccessToken: () => Promise<string | undefined>;
  setBrowserLocalPersistence: () => Promise<void>;
  verifyEmail: (oobCode: string) => Promise<'success'>;
  login: (arg0: {
    email?: string;
    password?: string;
    provider: 'emailAndPassword' | 'google';
    isSignup: boolean;
  }) => ReturnType<AuthProvider['login']>;
  getCurrentUser: () => User | null;
  updateUserProfile: (payload: {
    displayName?: string | null;
    photoURL?: string | null;
  }) => Promise<void>;
  sendPasswordResetLink: (payload: {
    email: string;
    token: string;
    action: string;
  }) => Promise<void>;
  verifyPasswordResetCode: (oobCode: string) => Promise<string>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
}

export const authProvider: ExtendedAuthProvider = {
  login: async ({ email = '', password = '', provider, isSignup }) => {
    let userCredential;

    switch (provider) {
      case 'emailAndPassword':
        if (isSignup) {
          userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );
        } else {
          userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );
        }
        break;
      case 'google':
        userCredential = await signInWithPopup(auth, new GoogleAuthProvider());
        break;
    }

    if (!userCredential) {
      return Promise.reject('Authentication failed');
    }

    if (!userCredential.user?.emailVerified) {
      await sendEmailVerification(userCredential.user);
      return Promise.reject({ hasToVerifyEmail: true });
    }

    let permissions;
    try {
      permissions = await getDashboardPermissions();
    } catch (e) {
      await signOut(auth);
      return Promise.reject({
        message: 'Your account does not have access to this platform.',
      });
    }
    localStorage.setItem('permissions', JSON.stringify(permissions));

    const userProfile = await getUserProfile();
    if (userProfile) {
      const firstAdvertiser = userProfile.advertisers[0];
      if (firstAdvertiser) {
        return Promise.resolve({
          redirectTo: `/advertiser/${firstAdvertiser.id}/campaigns`,
        });
      }
      return Promise.reject({ redirectTo: `/login`, code: 'no_advertisers' });
    }
    return Promise.resolve({ redirectTo: '/complete-profile' });
  },

  logout: async () => {
    await auth.signOut();
    localStorage.removeItem('permissions');
    await signOut(auth);
  },

  checkAuth: () => {
    return new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged(user => {
        unsubscribe();
        if (user && localStorage.getItem('permissions')) {
          resolve();
        } else {
          reject({ message: false });
        }
      });
    });
  },

  checkError: async error => {
    console.error('check error', error);
    const status = error.status;
    if (status === 401 || status === 403) {
      await auth.signOut();
      localStorage.removeItem('permissions');
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getIdentity: () => {
    if (auth.currentUser) {
      return Promise.resolve({
        id: auth.currentUser.uid,
        fullName: auth.currentUser.displayName,
        avatar: auth.currentUser.photoURL,
        email: auth.currentUser.email,
        emailVerified: auth.currentUser.emailVerified,
      } as UserIdentity);
    }
    return Promise.reject('No user found');
  },

  getPermissions: () => {
    const permissions = localStorage.getItem('permissions');
    return permissions
      ? Promise.resolve(JSON.parse(permissions))
      : Promise.resolve([]);
  },

  canAccess: ({ resource, action }) => {
    const storedData = localStorage.getItem('permissions');

    const permissions = storedData ? JSON.parse(storedData) : [];
    return Promise.resolve(
      canAccessWithPermissions({ permissions, action, resource })
    );
  },

  verifyEmail: async oobCode => {
    await applyActionCode(auth, oobCode);
    return Promise.resolve('success');
  },
  getAccessToken: async () => {
    return auth.currentUser?.getIdToken();
  },
  setBrowserLocalPersistence: async () => {
    await auth.setPersistence(browserLocalPersistence);
  },
  getCurrentUser: () => {
    return auth.currentUser;
  },
  updateUserProfile: async payload => {
    if (auth.currentUser) {
      return updateProfile(auth.currentUser, payload);
    }
    return Promise.reject('No user found');
  },
  sendPasswordResetLink: async payload => {
    await fetchUtils.fetchJson(
      `${API_URL}/advertiser-users/send-password-reset-link`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    return Promise.resolve();
  },
  verifyPasswordResetCode: async code => {
    return verifyPasswordResetCode(auth, code);
  },
  confirmPasswordReset: async (oobCode, newPassword) => {
    return confirmPasswordReset(auth, oobCode, newPassword);
  },
  sendEmailVerification: async () => {
    if (!auth.currentUser) {
      throw new Error('No user found');
    }
    return sendEmailVerification(auth.currentUser);
  },
};
